/**
 * Coarse side-view signals from a grayscale frame.
 * This is not face recognition and not a person-identification model.
 * A "person" is a tall textured column. A desk object is a new small
 * textured patch in the lower half after a baseline mask is supplied.
 */

export interface RoomFrameSignals {
  luma: number;
  sceneDelta: number;
  personCount: number;
  deskObject: boolean;
  deskMask: Uint8Array;
  placement: 'ok' | 'too_dark' | 'too_uniform';
}

const COLS = 10;
const ROWS = 8;

function cellStd(
  gray: Uint8Array,
  width: number,
  height: number,
  x0: number,
  y0: number,
  cellW: number,
  cellH: number
) {
  let sum = 0;
  let sumSq = 0;
  let count = 0;
  const x1 = Math.min(width, x0 + cellW);
  const y1 = Math.min(height, y0 + cellH);
  for (let y = y0; y < y1; y += 1) {
    for (let x = x0; x < x1; x += 1) {
      const value = gray[y * width + x] || 0;
      sum += value;
      sumSq += value * value;
      count += 1;
    }
  }
  if (!count) return 0;
  const mean = sum / count;
  return Math.sqrt(Math.max(0, sumSq / count - mean * mean));
}

function components(occupied: Uint8Array) {
  const seen = new Uint8Array(occupied.length);
  const found: Array<{ minRow: number; maxRow: number; minCol: number; maxCol: number; cells: number[] }> = [];
  for (let index = 0; index < occupied.length; index += 1) {
    if (!occupied[index] || seen[index]) continue;
    const stack = [index];
    seen[index] = 1;
    const cells: number[] = [];
    let minRow = ROWS;
    let maxRow = 0;
    let minCol = COLS;
    let maxCol = 0;
    while (stack.length) {
      const current = stack.pop() as number;
      const row = Math.floor(current / COLS);
      const col = current % COLS;
      cells.push(current);
      minRow = Math.min(minRow, row);
      maxRow = Math.max(maxRow, row);
      minCol = Math.min(minCol, col);
      maxCol = Math.max(maxCol, col);
      const neighbors = [current - 1, current + 1, current - COLS, current + COLS];
      for (const next of neighbors) {
        if (next < 0 || next >= occupied.length || seen[next] || !occupied[next]) continue;
        const nextCol = next % COLS;
        if (Math.abs(nextCol - col) > 1) continue;
        seen[next] = 1;
        stack.push(next);
      }
    }
    found.push({ minRow, maxRow, minCol, maxCol, cells });
  }
  return found;
}

export function analyzeRoomGray(
  gray: Uint8Array,
  width: number,
  height: number,
  previous: Uint8Array | null = null,
  baselineDesk: Uint8Array | null = null
): RoomFrameSignals {
  const safeWidth = Math.max(1, width);
  const safeHeight = Math.max(1, height);
  let lumaSum = 0;
  const step = Math.max(1, Math.floor(gray.length / 4000));
  let samples = 0;
  for (let index = 0; index < gray.length; index += step) {
    lumaSum += gray[index] || 0;
    samples += 1;
  }
  const luma = samples ? lumaSum / samples : 0;

  let sceneDelta = 0;
  if (previous && previous.length === gray.length && gray.length) {
    let changed = 0;
    let compared = 0;
    for (let index = 0; index < gray.length; index += step) {
      compared += 1;
      if (Math.abs((gray[index] || 0) - (previous[index] || 0)) > 18) changed += 1;
    }
    sceneDelta = compared ? changed / compared : 0;
  }

  const cellW = Math.max(1, Math.floor(safeWidth / COLS));
  const cellH = Math.max(1, Math.floor(safeHeight / ROWS));
  const occupied = new Uint8Array(COLS * ROWS);
  let maxStd = 0;
  for (let row = 0; row < ROWS; row += 1) {
    for (let col = 0; col < COLS; col += 1) {
      const std = cellStd(gray, safeWidth, safeHeight, col * cellW, row * cellH, cellW, cellH);
      maxStd = Math.max(maxStd, std);
      if (std >= 14) occupied[row * COLS + col] = 1;
    }
  }

  const blobs = components(occupied);
  const people = blobs.filter((blob) => blob.maxRow - blob.minRow + 1 >= 3);
  const personCount = people.length > 0 ? Math.min(3, people.length) : (luma > 22 ? 0 : 0);

  const deskMask = new Uint8Array(COLS * ROWS);
  for (const blob of blobs) {
    const tall = blob.maxRow - blob.minRow + 1 >= 3;
    if (tall || blob.cells.length > 6) continue;
    for (const cell of blob.cells) {
      if (Math.floor(cell / COLS) >= 4) deskMask[cell] = 1;
    }
  }

  let deskObject = false;
  if (baselineDesk && baselineDesk.length === deskMask.length) {
    for (let index = 0; index < deskMask.length; index += 1) {
      if (deskMask[index] && !baselineDesk[index]) {
        deskObject = true;
        break;
      }
    }
  }

  let placement: RoomFrameSignals['placement'] = 'ok';
  if (luma <= 18) placement = 'too_dark';
  else if (maxStd < 8) placement = 'too_uniform';

  return { luma, sceneDelta, personCount, deskObject, deskMask, placement };
}

export function grayFromImageData(data: Uint8ClampedArray, width: number, height: number) {
  const gray = new Uint8Array(width * height);
  for (let index = 0; index < gray.length; index += 1) {
    const offset = index * 4;
    gray[index] = Math.round(
      0.2126 * data[offset] + 0.7152 * data[offset + 1] + 0.0722 * data[offset + 2]
    );
  }
  return gray;
}
